import React, { useMemo, useState } from "react"

import { Input } from "@coval/ui"
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Plus, Trash2 } from "lucide-react"

import { ConfirmationDialog } from "@/components/shared/alerts/ConfirmDialog"
import { cn } from "@/utils/cn"
import type { ComparisonPlotConfig } from "@/utils/comparisonPlot"
import {
  addPlotToLayout,
  applyMetricLayout,
  metricLayoutItemId,
  userSectionId,
  type GroupedMetrics,
  type MetricLayoutConfig,
  type MetricLayoutItem,
  type MetricLayoutSection,
} from "@/utils/metricLayout"

const SECTION_PREFIX = "section:"

interface MetricLayoutSectionsProps {
  groupedMetrics: GroupedMetrics
  metricLayout: MetricLayoutConfig | undefined
  editLayout: boolean
  onMetricLayoutChange?: (layout: MetricLayoutConfig) => void
  useSingleColumn: boolean
  /** Renders a single metric card (caller owns interactivity / drill-in). */
  renderCard: (metricId: string) => React.ReactNode
  /** Renders a comparison-plot block. Absent on surfaces without plots. */
  renderPlotBlock?: (plot: ComparisonPlotConfig) => React.ReactNode
  /** Builds a new plot config; when set, an "Add plot" control is shown. */
  createPlot?: () => ComparisonPlotConfig
  /** Notifies the new plot's id so the caller can select it for editing. */
  onPlotAdded?: (plotId: string) => void
}

// Which items render full width in two-column mode: plots always; a metric card
// that is alone in its row (single-card sections, last-odd cards, or a card a
// plot/section-edge breaks onto its own row) spans both columns.
function computeFullWidth(
  items: MetricLayoutItem[],
  useSingleColumn: boolean,
): boolean[] {
  if (useSingleColumn) {
    return items.map(() => false)
  }
  const fullWidth = items.map((item) => item.kind === "plot")
  let col = 0
  for (let i = 0; i < items.length; i++) {
    if (fullWidth[i]) {
      col = 0
      continue
    }
    const next = items[i + 1]
    const rowEndsAfter = next === undefined || next.kind === "plot"
    if (col === 0 && rowEndsAfter) {
      fullWidth[i] = true
      col = 0
    } else {
      col = col === 0 ? 1 : 0
    }
  }
  return fullWidth
}

function gridClass(useSingleColumn: boolean): string {
  return cn("grid gap-4", useSingleColumn ? "grid-cols-1" : "grid-cols-2")
}

function indexOfItem(items: MetricLayoutItem[], id: string): number {
  return items.findIndex((item) => metricLayoutItemId(item) === id)
}

// View mode: today's static layout (section heading + responsive grid). Plots
// render full width; metric cards keep the prior 1/2-column flow. No drag.
function StaticSections({
  sections,
  useSingleColumn,
  renderItem,
}: {
  sections: MetricLayoutSection[]
  useSingleColumn: boolean
  renderItem: (item: MetricLayoutItem) => React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-4">
      {sections.map((section) => {
        const fullWidth = computeFullWidth(section.items, useSingleColumn)
        return (
          <div key={section.id} className="flex flex-col gap-2">
            <h2 className="px-1 text-sm font-normal text-coval-text-muted">
              {section.title}
            </h2>
            <div className={gridClass(useSingleColumn)}>
              {section.items.map((item, index) => (
                <div
                  key={metricLayoutItemId(item)}
                  className={fullWidth[index] ? "col-span-2" : undefined}
                >
                  {renderItem(item)}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SortableItem({
  id,
  fullWidth,
  children,
}: {
  id: string
  fullWidth: boolean
  children: React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className={cn("flex items-start gap-1", fullWidth && "col-span-2")}
    >
      <button
        type="button"
        className="mt-2 flex-shrink-0 cursor-grab touch-none text-coval-text-muted active:cursor-grabbing"
        aria-label="Reorder item"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

function SortableSection({
  section,
  useSingleColumn,
  renderItem,
  onRename,
  onRemove,
}: {
  section: MetricLayoutSection
  useSingleColumn: boolean
  renderItem: (item: MetricLayoutItem) => React.ReactNode
  onRename: (id: string, title: string) => void
  onRemove: (id: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${SECTION_PREFIX}${section.id}` })
  // The item grid is a droppable keyed by the section id so items can be dropped
  // into an empty section or its gaps.
  const { setNodeRef: setDropRef } = useDroppable({ id: section.id })
  const fullWidth = computeFullWidth(section.items, useSingleColumn)

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="flex flex-col gap-2"
    >
      <div className="flex items-center gap-1 px-1">
        <button
          type="button"
          className="cursor-grab touch-none text-coval-text-muted active:cursor-grabbing"
          aria-label={`Reorder ${section.title} section`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
        <Input
          aria-label="Section title"
          value={section.title}
          onChange={(e) => onRename(section.id, e.target.value)}
          className="h-7 max-w-60 text-sm"
        />
        <button
          type="button"
          onClick={() => onRemove(section.id)}
          className="ml-auto p-1 text-coval-text-muted hover:text-coval-danger-text"
          aria-label={`Remove ${section.title} section`}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <SortableContext
        items={section.items.map(metricLayoutItemId)}
        strategy={rectSortingStrategy}
      >
        <div
          ref={setDropRef}
          className={cn(gridClass(useSingleColumn), "min-h-12")}
        >
          {section.items.length === 0 && (
            <div
              className={cn(
                "rounded-lg border border-dashed border-coval-border px-3 py-6 text-center text-xs text-coval-text-muted",
                !useSingleColumn && "col-span-2",
              )}
            >
              Drag cards here
            </div>
          )}
          {section.items.map((item, index) => (
            <SortableItem
              key={metricLayoutItemId(item)}
              id={metricLayoutItemId(item)}
              fullWidth={fullWidth[index]}
            >
              {renderItem(item)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </div>
  )
}

export const MetricLayoutSections: React.FC<MetricLayoutSectionsProps> = ({
  groupedMetrics,
  metricLayout,
  editLayout,
  onMetricLayoutChange,
  useSingleColumn,
  renderCard,
  renderPlotBlock,
  createPlot,
  onPlotAdded,
}) => {
  const derived = useMemo(
    () => applyMetricLayout(metricLayout, groupedMetrics),
    [metricLayout, groupedMetrics],
  )

  // During a drag we keep a local working copy so cross-section moves preview
  // live; on drag end we commit to the parent. Outside a drag, render derived.
  const [working, setWorking] = useState<MetricLayoutSection[] | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const renderItem = (item: MetricLayoutItem): React.ReactNode =>
    item.kind === "plot"
      ? (renderPlotBlock?.(item.plot) ?? null)
      : renderCard(item.metricId)

  if (!editLayout) {
    // When plots can't render (feature off), drop them so the layout doesn't
    // reserve empty cells for blocks that never paint.
    const staticSections = renderPlotBlock
      ? derived
      : derived.map((section) => ({
          ...section,
          items: section.items.filter((item) => item.kind !== "plot"),
        }))
    return (
      <StaticSections
        sections={staticSections}
        useSingleColumn={useSingleColumn}
        renderItem={renderItem}
      />
    )
  }

  const sections = working ?? derived
  const commit = (next: MetricLayoutSection[]) =>
    onMetricLayoutChange?.({ sections: next })
  const reset = () => {
    setWorking(null)
    setActiveId(null)
  }

  const isSectionDrag = (id: string) => id.startsWith(SECTION_PREFIX)
  const sectionIdOf = (id: string): string | undefined => {
    if (sections.some((section) => section.id === id)) {
      return id
    }
    return sections.find((section) => indexOfItem(section.items, id) >= 0)?.id
  }

  const addSection = () =>
    commit([
      ...sections,
      { id: userSectionId(), title: "New section", items: [] },
    ])
  const addPlot = () => {
    if (!createPlot) {
      return
    }
    const plot = createPlot()
    commit(addPlotToLayout(sections, plot))
    onPlotAdded?.(plot.id)
  }
  const renameSection = (id: string, title: string) =>
    commit(
      sections.map((section) =>
        section.id === id ? { ...section, title } : section,
      ),
    )
  const performRemove = (id: string) =>
    commit(sections.filter((section) => section.id !== id))
  // Removing a section re-homes its metric cards to their default category (not
  // destructive), but its plot blocks are deleted — so confirm when non-empty.
  const removeSection = (id: string) => {
    const section = sections.find((candidate) => candidate.id === id)
    if (section && section.items.length > 0) {
      setPendingRemoveId(id)
      return
    }
    performRemove(id)
  }
  const pendingRemove = pendingRemoveId
    ? sections.find((section) => section.id === pendingRemoveId)
    : undefined

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
    setWorking(derived)
  }

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) {
      return
    }
    const activeKey = String(active.id)
    const overKey = String(over.id)
    if (isSectionDrag(activeKey)) {
      return
    }
    const from = sectionIdOf(activeKey)
    const to = sectionIdOf(overKey)
    if (!from || !to || from === to) {
      return
    }
    setWorking((prev) => {
      const base = (prev ?? derived).map((section) => ({
        ...section,
        items: [...section.items],
      }))
      const fromSection = base.find((section) => section.id === from)
      const toSection = base.find((section) => section.id === to)
      if (!fromSection || !toSection) {
        return prev
      }
      const fromIndex = indexOfItem(fromSection.items, activeKey)
      if (fromIndex < 0) {
        return prev
      }
      const [moved] = fromSection.items.splice(fromIndex, 1)
      const overIndex = indexOfItem(toSection.items, overKey)
      toSection.items.splice(
        overIndex >= 0 ? overIndex : toSection.items.length,
        0,
        moved,
      )
      return base
    })
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    const base = working ?? derived
    const activeKey = String(active.id)
    let next = base

    if (isSectionDrag(activeKey)) {
      const activeSectionId = activeKey.slice(SECTION_PREFIX.length)
      const overKey = over ? String(over.id) : null
      const targetSectionId = overKey
        ? isSectionDrag(overKey)
          ? overKey.slice(SECTION_PREFIX.length)
          : sectionIdOf(overKey)
        : null
      if (targetSectionId && targetSectionId !== activeSectionId) {
        const oldIndex = base.findIndex((s) => s.id === activeSectionId)
        const newIndex = base.findIndex((s) => s.id === targetSectionId)
        if (oldIndex >= 0 && newIndex >= 0) {
          next = arrayMove(base, oldIndex, newIndex)
        }
      }
    } else if (over) {
      // Item: finalize ordering within its (possibly new) section. Cross-section
      // moves were already applied to `base` during onDragOver.
      const overKey = String(over.id)
      const container = sectionIdOf(activeKey)
      const overContainer = sectionIdOf(overKey)
      if (container && overContainer && container === overContainer) {
        next = base.map((section) => {
          if (section.id !== container) {
            return section
          }
          const oldIndex = indexOfItem(section.items, activeKey)
          const newIndex = indexOfItem(section.items, overKey)
          if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
            return section
          }
          return {
            ...section,
            items: arrayMove(section.items, oldIndex, newIndex),
          }
        })
      }
    }

    // Only persist when the arrangement actually changed, so a plain
    // grab-and-release or a same-position drop never marks the report dirty.
    if (JSON.stringify(next) !== JSON.stringify(derived)) {
      commit(next)
    }
    reset()
  }

  const activeItem: MetricLayoutItem | undefined =
    activeId && !isSectionDrag(activeId)
      ? sections
          .flatMap((section) => section.items)
          .find((item) => metricLayoutItemId(item) === activeId)
      : undefined
  const activeSection =
    activeId && isSectionDrag(activeId)
      ? sections.find((s) => s.id === activeId.slice(SECTION_PREFIX.length))
      : null

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={reset}
      >
        <SortableContext
          items={sections.map((section) => `${SECTION_PREFIX}${section.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-4">
            {sections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                useSingleColumn={useSingleColumn}
                renderItem={renderItem}
                onRename={renameSection}
                onRemove={removeSection}
              />
            ))}
            <div className="flex items-center gap-4 self-start px-1 py-2">
              <button
                type="button"
                onClick={addSection}
                className="flex items-center gap-1 text-sm text-coval-text-muted hover:text-coval-text"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add section
              </button>
              {createPlot && (
                <button
                  type="button"
                  onClick={addPlot}
                  className="flex items-center gap-1 text-sm text-coval-text-muted hover:text-coval-text"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add plot
                </button>
              )}
            </div>
          </div>
        </SortableContext>
        <DragOverlay>
          {activeItem ? (
            <div className="opacity-90 shadow-lg">{renderItem(activeItem)}</div>
          ) : activeSection ? (
            <div className="rounded-md bg-surface-1 px-3 py-1.5 text-sm font-medium text-coval-text shadow-lg">
              {activeSection.title}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <ConfirmationDialog
        showConfirmDialog={pendingRemove !== undefined}
        setShowConfirmDialog={(show) => {
          if (!show) {
            setPendingRemoveId(null)
          }
        }}
        title="Remove section?"
        description={pendingRemove ? describeSectionRemoval(pendingRemove) : ""}
        confirmLabel="Remove"
        handleConfirmChange={() => {
          if (pendingRemoveId) {
            performRemove(pendingRemoveId)
          }
          setPendingRemoveId(null)
        }}
        handleCancelChange={() => setPendingRemoveId(null)}
      />
    </>
  )
}

function describeSectionRemoval(section: MetricLayoutSection): string {
  const metricCount = section.items.filter(
    (item) => item.kind === "metric",
  ).length
  const plotCount = section.items.length - metricCount
  const parts: string[] = []
  if (metricCount > 0) {
    parts.push(
      `${metricCount} card${metricCount === 1 ? "" : "s"} will move back to their default category`,
    )
  }
  if (plotCount > 0) {
    parts.push(`${plotCount} plot${plotCount === 1 ? "" : "s"} will be removed`)
  }
  return `"${section.title}": ${parts.join("; ")}.`
}
